import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CloseButtonIcon } from '../../../../public/Images';
import SwipeableDrawer from '@/components/shared/bottom-sheet/bottom-sheet.component';
import CarDisplayCardClient from '@/components/shared/car-details-card/car-details-car-client.component';
import Typography from '@/components/shared/typography/typography.component';
import Image from 'next/image';
import { Box } from '@mui/material';
import colors from '@/theme/colors.module.scss';
import {
    defaultSearchTermsResponse,
    getDefaultIClientRetailFilters,
} from '@/types';
import { CarListingData } from '@/types/cars.types';
import { getCarListingsElastic } from '@/api-requests/car-listing-elastic.apis';
import { convertClientToEsFilters } from '@/utils/filterUtil';
import useDebounce from '@/hook/debounce.hook';
import { getCarCardLocale } from '@/utils';
import { AVAILABLE_LANGUAGES } from '@/constants';
import { PurchaseCarSteps } from '@/components/purchaseCarSteps';
import { CarDisplayCardV2Skeleton } from '@/components/shared/car-details-card-v2/car-details-car-client-skeleton.component';

const BottomSheetMobileAvailableCars = ({
    open,
    setOpen,
    priceToSearch,
}: {
    priceToSearch: number;
    open: boolean;
    setOpen: (flag: boolean) => void;
}) => {
    const [loading, setLoading] = useState(true);

    const {
        t,
        i18n: { language },
    } = useTranslation();
    const [displayedListings, setDisplayedListing] = useState<CarListingData[]>(
        []
    );

    const loadVehicles = useDebounce(() => {
        setLoading(true);
        const filters = getDefaultIClientRetailFilters();
        filters.price.min = (priceToSearch * 4) / 5;
        filters.price.max = (priceToSearch * 6) / 5;
        getCarListingsElastic({
            filters: convertClientToEsFilters(filters, {
                ...defaultSearchTermsResponse,
            }),
        })
            .then(newCars => {
                if (newCars.data.length) {
                    setDisplayedListing(newCars.data);
                }
            })
            .catch(err => {
                // eslint-disable-next-line no-console
                console.log(err);
            })
            .finally(() => {
                setLoading(false);
            });
    }, 400);
    useEffect(() => {
        loadVehicles();
    }, [priceToSearch]);

    const carCardLocalizations = getCarCardLocale();
    const sortedCars = useMemo(() => {
        if (displayedListings.length === 0) {
            return [];
        }
        return displayedListings.map((item, index) => (
            <React.Fragment key={`${item.id + index}-frgmnt`}>
                <Box
                    key={item.id + index}
                    sx={{
                        height: 'fit-content',
                    }}
                >
                    <CarDisplayCardClient
                        carCardLocalizations={carCardLocalizations}
                        lang={AVAILABLE_LANGUAGES[language]}
                        id={item.id}
                        carData={item}
                        discountPrice={item.discountPrice}
                        isPurchaseInProgress={item.isPurchaseInProgress}
                        key={index + item.id + 'car_finance'}
                        cardTitle={`${t(item.make)} ${item.year} ${item.model}`}
                        carName={item.sfVehicleName}
                        trim={item.trim}
                        installmentPrice={item?.installmentPrice}
                        totalPrice={item.sylndrSellingPrice}
                        kilometers={item.kilometers}
                        year={item.year}
                        transmission={item.transmission}
                        images={item.images}
                        isReserved={item.isReserved}
                        make={item.make}
                        model={item.model}
                        sourceScreen={'Cars Listing'}
                        appliedFilter={false}
                        appliedSort={false}
                        position={index + 1}
                        isNewlyAdded={item.isNewlyAdded}
                        tags={item.tags}
                    />
                </Box>

                {(displayedListings.length < 6 &&
                    index === displayedListings.length - 1) ||
                index === 4 ? (
                    <Box
                        sx={{
                            flex: 1,
                        }}
                    >
                        <PurchaseCarSteps />
                        {/*<ReserveSeeCarBanner variant={'banner'} />*/}
                    </Box>
                ) : (
                    ''
                )}
            </React.Fragment>
        ));
    }, [displayedListings]);
    return (
        <Box>
            <SwipeableDrawer open={open} setOpen={setOpen}>
                <Box sx={{ height: '95vh', position: 'relative' }}>
                    <Box
                        sx={{
                            marginBottom: '13px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Typography
                            variant="subtitle2Medium"
                            color={colors.gray900}
                        >
                            {`${sortedCars.length}  ${t(
                                'Sylndr certified cars sort'
                            )}`}
                        </Typography>
                        <Box
                            onClick={() => {
                                setOpen(false);
                            }}
                        >
                            <Image
                                src={CloseButtonIcon}
                                alt="close"
                                width={24}
                                height={24}
                            />
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                        }}
                    >
                        {loading ? (
                            <Box sx={{ padding: ' 3vw 0px' }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        position: 'relative',
                                    }}
                                >
                                    <Box sx={{ width: '100%' }}>
                                        <Box>
                                            <Box
                                                sx={{
                                                    display: 'grid',
                                                    gridTemplateColumns: `repeat(${1}, 1fr)`,
                                                    gap: '12px 4px',
                                                    width: '100%',
                                                }}
                                            >
                                                {Array.from({ length: 4 }).map(
                                                    (_, index) => (
                                                        <CarDisplayCardV2Skeleton
                                                            key={`finance-skeleton-${index}`}
                                                        />
                                                    )
                                                )}
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        ) : (
                            <Box sx={{ padding: ' 3vw 0px' }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        position: 'relative',
                                    }}
                                >
                                    <Box sx={{ width: '100%' }}>
                                        <Box>
                                            <Box
                                                sx={{
                                                    display: 'grid',
                                                    gridTemplateColumns: `repeat(${1}, 1fr)`,
                                                    gap: '12px 4px',
                                                    width: '100%',
                                                }}
                                            >
                                                {sortedCars}
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Box>
            </SwipeableDrawer>
        </Box>
    );
};

export default BottomSheetMobileAvailableCars;
